import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useGithub } from '../hooks/useGithub';

const AuthCallbackPage: React.FC = () => {
  const { login } = useGithub();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  
  useEffect(() => {
    const authenticateWithGitHub = async () => {
      // Get code from URL
      const searchParams = new URLSearchParams(location.search);
      const code = searchParams.get('code');
      
      if (!code) {
        setError('No authorization code received from GitHub');
        setIsLoading(false);
        return;
      }
      
      try {
        // Call login with the code
        await login(code);
        setIsAuthenticated(true);
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Authentication failed');
      } finally {
        setIsLoading(false);
      }
    };
    
    authenticateWithGitHub();
  }, [location, login]);
  
  if (isAuthenticated) {
    return <Navigate to="/repositories" />;
  }
  
  return (
    <div className="container mx-auto px-4 py-20">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-8">
        <h1 className="text-2xl font-bold mb-4 text-center">
          {isLoading ? 'Authenticating with GitHub...' : 'Authentication Complete'}
        </h1>
        
        {isLoading && (
          <div className="flex justify-center items-center my-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        )}
        
        {error && (
          <div className="bg-red-50 text-red-800 p-4 rounded-md mb-4">
            <p className="font-medium">Authentication Error</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        )}
        
        {!isLoading && !error && !isAuthenticated && (
          <div className="bg-yellow-50 text-yellow-800 p-4 rounded-md mb-4">
            <p className="font-medium">Authentication in Progress</p>
            <p className="text-sm mt-1">
              Please wait while we complete the authentication process.
            </p>
          </div>
        )}
        
        {!isLoading && (
          <div className="flex justify-center mt-4">
            <a href="/" className="github-btn">
              Return to Home
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthCallbackPage; 